// src/pages/produtos/grupos/GrupoImagemModal.js
import React, { useEffect, useRef, useState } from 'react';
import ModalBody from '../../../components/modalForm/ModalBody';
import ModalContainer from '../../../components/modalForm/ModalContainer';
import ModalFooter from '../../../components/modalForm/ModalFooter';
import ModalHeader from '../../../components/modalForm/ModalHeader';
import { useMessage } from '../../../contexts/geral/MessageContext';
import { obterArquivoImagemGrupo } from '../../../services/produtos/ImagemService';
import { CATALOG_IMAGE_ACCEPT, processCatalogImageFile } from '../../../utils/ImageUtils.js';

const MAX_IMAGE_HEIGHT = 300;

const getImageMimeType = (fileName = '') => {
    const extension = fileName.split('.').pop()?.toLowerCase();

    if (extension === 'png') return 'image/png';
    if (extension === 'webp') return 'image/webp';
    if (extension === 'gif') return 'image/gif';

    return 'image/jpeg';
};

const GrupoImagemModal = ({
    show,
    onHide,
    grupo,
    setGrupo,
}) => {
    const { showMessage } = useMessage();
    const [imagemPreview, setImagemPreview] = useState(null);
    const [imagemSel, setImagemSel] = useState(null);
    const [carregandoImagem, setCarregandoImagem] = useState(false);
    const objectUrlRef = useRef(null);
    const carregamentoRef = useRef(0);

    const revogarObjectUrl = () => {
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
    };

    const definirPreviewBlob = (blob) => {
        revogarObjectUrl();
        objectUrlRef.current = URL.createObjectURL(blob);
        setImagemPreview(objectUrlRef.current);
    };

    useEffect(() => () => {
        carregamentoRef.current += 1;
        revogarObjectUrl();
    }, []);

    useEffect(() => {
        let ativo = true;

        if (!show) {
            carregamentoRef.current += 1;
            revogarObjectUrl();
            return () => {
                ativo = false;
            };
        }

        carregamentoRef.current += 1;
        setCarregandoImagem(false);

        if (grupo?.imagemGrupo?.arquivo) {
            revogarObjectUrl();
            setImagemSel(grupo.imagemGrupo);
            setImagemPreview(`data:${getImageMimeType(grupo.imagemGrupo.nome)};base64,${grupo.imagemGrupo.arquivo}`);
            return () => {
                ativo = false;
            };
        }

        revogarObjectUrl();
        setImagemSel(grupo?.imagemGrupo?.nome ? grupo.imagemGrupo : null);
        setImagemPreview(null);

        if (grupo?.imagemGrupoId) {
            const carregamentoAtual = ++carregamentoRef.current;
            setCarregandoImagem(true);
            obterArquivoImagemGrupo(grupo.id)
                .then((result) => {
                    if (!ativo || carregamentoAtual !== carregamentoRef.current || !result) return;

                    definirPreviewBlob(result.blob);
                    setImagemSel({
                        id: grupo.imagemGrupoId,
                        nome: result.nome || grupo.imagemGrupo?.nome || 'Imagem cadastrada',
                        tamanho: result.blob.size,
                        arquivo: null,
                    });
                })
                .catch(() => {
                    // Mantém o botão disponível para uma nova tentativa manual.
                })
                .finally(() => {
                    if (ativo && carregamentoAtual === carregamentoRef.current) {
                        setCarregandoImagem(false);
                    }
                });
        } else {
            setCarregandoImagem(false);
        }

        return () => {
            ativo = false;
        };
    }, [show, grupo?.id, grupo?.imagemGrupo?.arquivo, grupo?.imagemGrupo?.nome, grupo?.imagemGrupoId]);

    const handleImagemChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        carregamentoRef.current += 1;
        setCarregandoImagem(false);

        try {
            const imagemProcessada = await processCatalogImageFile(file, { maxHeight: MAX_IMAGE_HEIGHT });
            const imagemPayload = {
                id: grupo.imagemGrupoId ? grupo.imagemGrupoId : 0,
                nome: imagemProcessada.nome,
                tamanho: imagemProcessada.tamanho,
                arquivo: imagemProcessada.arquivo,
            };

            revogarObjectUrl();
            setImagemSel(imagemPayload);
            setImagemPreview(imagemProcessada.previewUrl);
            setGrupo({ ...grupo, imagemGrupoId: imagemPayload.id || grupo.imagemGrupoId, imagemGrupo: imagemPayload });
        } catch (error) {
            showMessage(error.message || 'Erro ao processar a imagem.', 'warning');
            e.target.value = '';
        }
    };

    const handleCarregaImagem = async (e) => {
        e?.preventDefault();

        if (!grupo?.imagemGrupoId) {
            return;
        }

        const carregamentoAtual = ++carregamentoRef.current;
        try {
            setCarregandoImagem(true);
            const result = await obterArquivoImagemGrupo(grupo.id);
            if (carregamentoAtual !== carregamentoRef.current) return;
            if (!result) {
                showMessage('Imagem do grupo não encontrada.', 'warning');
                return;
            }

            definirPreviewBlob(result.blob);
            setImagemSel({
                id: grupo.imagemGrupoId,
                nome: result.nome || grupo.imagemGrupo?.nome || 'Imagem cadastrada',
                tamanho: result.blob.size,
                arquivo: null,
            });
        } catch (error) {
            if (carregamentoAtual === carregamentoRef.current) {
                showMessage(error.message || 'Erro ao carregar imagem do grupo.', 'warning');
            }
        } finally {
            if (carregamentoAtual === carregamentoRef.current) {
                setCarregandoImagem(false);
            }
        }
    };

    if (!show) return null;

    return (
        <ModalContainer show={show} largura="600px" zIndex={1070}>
            <ModalHeader>
                <h5 className="modal-title">Imagem do Grupo</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={onHide} />
            </ModalHeader>
            <ModalBody>
                <div className="form-group">
                    <label>Imagem:</label>
                    <div className="d-flex align-items-center">
                        {(grupo.imagemGrupoId && !imagemPreview) && (
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleCarregaImagem}
                                disabled={carregandoImagem}
                            >
                                {carregandoImagem ? 'Carregando...' : 'Carregar Imagem'}
                            </button>
                        )}
                        <input
                            type="file"
                            accept={CATALOG_IMAGE_ACCEPT}
                            onChange={handleImagemChange}
                            disabled={carregandoImagem || (grupo.imagemGrupoId && !imagemPreview)}
                        />
                        <input
                            type="text"
                            className="form-control ml-2"
                            readOnly
                            value={imagemSel?.nome ? imagemSel.nome : ''}
                            disabled
                        />
                    </div>
                </div>
                {imagemPreview && (
                    <div className="mt-3">
                        <img src={imagemPreview} alt="Pre-visualizacao" style={{ maxHeight: '250px', maxWidth: '100%' }} />
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <button type="button" className="btn btn-secondary" onClick={onHide}>
                    Fechar
                </button>
            </ModalFooter>
        </ModalContainer>
    );
};

export default GrupoImagemModal;
